import React, { useState, useEffect } from "react";
import {
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcDiscover,
  FaPhone,
  FaEnvelope,
  FaCreditCard,
  FaHome,
  FaGlobe,
  FaLock,
  FaInfoCircle,
} from "react-icons/fa";
import SideBar from "../Components/SideBar1";
import countryList from "react-select-country-list";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BottomBar from "../Components/BottomBar";

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    cardHolderName: "",
    cardNumber: "",
    cvv: "",
    expiryDate: "",
    billingAddress: "",
    city: "",
    zipCode: "",
    email: "",
    phoneCountryCode: "+94",
    phoneNumber: "",
    country: "Sri Lanka",
    saveCard: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [showSavedCards, setShowSavedCards] = useState(false);
  const [cardType, setCardType] = useState(null);

  // Get full list of countries for the select dropdown.
  const countries = countryList().getData();

  // Simulate fetching saved cards (replace with an API call if needed)
  useEffect(() => {
    const mockSavedCards = [
      { id: 1, cardNumber: "****-****-****-4321", cardType: "visa", expiryDate: "12/25" },
      { id: 2, cardNumber: "****-****-****-5678", cardType: "mastercard", expiryDate: "09/26" },
    ];
    setSavedCards(mockSavedCards);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Determine the card type based on the card number.
  const detectCardType = (cardNumber) => {
    const cleanNumber = cardNumber.replace(/\D/g, "");
    if (!cleanNumber) return null;
    if (cleanNumber.startsWith("4")) return "visa";
    if (/^5[1-5]/.test(cleanNumber)) return "mastercard";
    if (/^3[47]/.test(cleanNumber)) return "amex";
    if (/^(6011|65|64[4-9])/.test(cleanNumber)) return "discover";
    return null;
  };

  // Format the card number: remove non-digits, limit length, and add hyphens.
  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    const detectedType = detectCardType(value);
    setCardType(detectedType);
    if (detectedType === "amex") {
      value = value.slice(0, 15);
    } else {
      value = value.slice(0, 16);
    }
    const formattedValue = value.match(/.{1,4}/g)?.join("-") || "";
    setSettings({ ...settings, cardNumber: formattedValue });
    if (errors.cardNumber) {
      setErrors((prev) => ({ ...prev, cardNumber: null }));
    }
  };

  // Format the expiry date input to MM/YY.
  const handleExpiryDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.slice(0, 4);
    if (value.length > 2) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    setSettings({ ...settings, expiryDate: value });
    if (errors.expiryDate) {
      setErrors((prev) => ({ ...prev, expiryDate: null }));
    }
  };

  // Handle CVV input with appropriate length depending on card type.
  const handleCvvChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (cardType === "amex") {
      value = value.slice(0, 4);
    } else {
      value = value.slice(0, 3);
    }
    setSettings({ ...settings, cvv: value });
    if (errors.cvv) {
      setErrors((prev) => ({ ...prev, cvv: null }));
    }
  };

  // Allow the user to select a saved card.
  const handleSelectCard = (card) => {
    setSettings({
      ...settings,
      cardNumber: card.cardNumber,
      expiryDate: card.expiryDate,
    });
    setCardType(card.cardType);
    setShowSavedCards(false);
    toast.info("Card selected. Please fill in the remaining details.");
  };

  // Validate the form fields.
  const validate = () => {
    const errors = {};
    const cleanedCardNumber = settings.cardNumber.replace(/-/g, "");
    if (!cleanedCardNumber) {
      errors.cardNumber = "Card number is required.";
    } else {
      if (cardType === "amex") {
        if (!/^\d{15}$/.test(cleanedCardNumber)) {
          errors.cardNumber = "American Express card number must be 15 digits.";
        }
      } else {
        if (!/^\d{16}$/.test(cleanedCardNumber)) {
          errors.cardNumber = "Card number must be 16 digits.";
        }
      }
    }
    if (!settings.cardHolderName) {
      errors.cardHolderName = "Card holder name is required.";
    }
    if (!settings.expiryDate) {
      errors.expiryDate = "Expiry date is required.";
    } else if (!/^\d{2}\/\d{2}$/.test(settings.expiryDate)) {
      errors.expiryDate = "Expiry date must be in MM/YY format.";
    } else {
      const [month, year] = settings.expiryDate.split("/").map(Number);
      const currentYear = new Date().getFullYear() % 100;
      const currentMonth = new Date().getMonth() + 1;
      if (month < 1 || month > 12) {
        errors.expiryDate = "Invalid month in expiry date.";
      } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
        errors.expiryDate = "Card has expired.";
      }
    }
    if (!settings.cvv) {
      errors.cvv = "CVV is required.";
    } else {
      if (cardType === "amex") {
        if (!/^\d{4}$/.test(settings.cvv)) {
          errors.cvv = "CVV must be 4 digits for American Express.";
        }
      } else {
        if (!/^\d{3}$/.test(settings.cvv)) {
          errors.cvv = "CVV must be 3 digits.";
        }
      }
    }
    if (!settings.billingAddress) {
      errors.billingAddress = "Billing address is required.";
    }
    if (!settings.city) {
      errors.city = "City is required.";
    }
    if (!settings.zipCode) {
      errors.zipCode = "ZIP/Postal code is required.";
    }
    if (!settings.email) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(settings.email)) {
      errors.email = "Email is invalid.";
    }
    if (!settings.phoneNumber) {
      errors.phoneNumber = "Phone number is required.";
    } else if (!/^\d+$/.test(settings.phoneNumber)) {
      errors.phoneNumber = "Phone number must contain only digits.";
    }
    return errors;
  };

  // Handle form submission and send data to the backend.
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors in the form.");
      return;
    }
    setLoading(true);
    const cleanedCardNumber = settings.cardNumber.replace(/-/g, "");
    // Build the payload according to your backend's SettingsDTO.
    const payload = {
      cardHolderName: settings.cardHolderName,
      cardNumber: cleanedCardNumber,
      expiryDate: settings.expiryDate,
      billingAddress: settings.billingAddress,
      city: settings.city,
      postalCode: settings.zipCode, // postalCode sent as String
      email: settings.email,
      phoneNumber: settings.phoneCountryCode + settings.phoneNumber,
      country: settings.country,
    };
    try {
      const response = await fetch("https://blockballot-production.up.railway.app/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Payment information saved successfully!");
        // Optionally, if saveCard is true, update savedCards locally.
        if (settings.saveCard) {
          const newCard = {
            id: savedCards.length + 1,
            cardNumber: "****-****-****-" + cleanedCardNumber.slice(-4),
            cardType: cardType,
            expiryDate: settings.expiryDate,
          };
          setSavedCards([...savedCards, newCard]);
          toast.info("Card has been saved for future use.");
        }
        // Reset form after successful submission.
        setSettings({
          cardHolderName: "",
          cardNumber: "",
          cvv: "",
          expiryDate: "",
          billingAddress: "",
          city: "",
          zipCode: "",
          email: "",
          phoneCountryCode: "+94",
          phoneNumber: "",
          country: "Sri Lanka",
          saveCard: false,
        });
      } else {
        toast.error("Error saving payment information. Please try again.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again later.");
    }
    setLoading(false);
  };

  // Render the appropriate card icon based on detected card type.
  const renderCardIcon = () => {
    if (!cardType) return null;
    switch (cardType) {
      case "visa":
        return <FaCcVisa className="text-blue-600 text-xl mr-3" />;
      case "mastercard":
        return <FaCcMastercard className="text-red-600 text-xl mr-3" />;
      case "amex":
        return <FaCcAmex className="text-green-600 text-xl mr-3" />;
      case "discover":
        return <FaCcDiscover className="text-orange-600 text-xl mr-3" />;
      default:
        return null;
    }
  };

  return (
      <div className="flex flex-col md:flex-row min-h-screen bg-bgColor font-montserrat ml-16">
        <SideBar />
        <div className="min-h-screen w-full p-4 md:p-10 md:ml-72">
          <ToastContainer position="top-right" autoClose={3000} />
          <div className="shadow-md rounded-xl p-4 md:p-8 w-full max-w-4xl mx-auto border bg-white border-gray-300">
            <h1 className="text-2xl md:text-3xl font-bold text-center mb-4">Payment Settings</h1>
            <hr className="mt-5 mb-6 border-t-2 border-black w-1/12 mx-auto" />

            {/* Saved Cards Section */}
            {savedCards.length > 0 && (
                <div className="mb-8">
                  <button
                      type="button"
                      onClick={() => setShowSavedCards(!showSavedCards)}
                      className="flex items-center text-blue-600 hover:text-blue-800 mb-2"
                  >
                    <FaCreditCard className="mr-2" />
                    {showSavedCards ? "Hide saved cards" : "Use a saved card"}
                  </button>
                  {showSavedCards && (
                      <div className="mt-2 grid grid-cols-1 gap-4">
                        {savedCards.map((card) => (
                            <div
                                key={card.id}
                                onClick={() => handleSelectCard(card)}
                                className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 flex items-center"
                            >
                              {card.cardType === "visa" && <FaCcVisa className="text-blue-600 text-2xl mr-3" />}
                              {card.cardType === "mastercard" && <FaCcMastercard className="text-red-600 text-2xl mr-3" />}
                              {card.cardType === "amex" && <FaCcAmex className="text-green-600 text-2xl mr-3" />}
                              <div>
                                <p className="font-medium">{card.cardNumber}</p>
                                <p className="text-sm text-gray-600">Expires: {card.expiryDate}</p>
                              </div>
                            </div>
                        ))}
                      </div>
                  )}
                </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Card Information Section */}
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaCreditCard className="mr-2 text-blue-600" /> Card Information
                  </h2>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Card Holder Name</label>
                  <input
                      type="text"
                      name="cardHolderName"
                      value={settings.cardHolderName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full border rounded-full px-4 py-2 bg-transparent"
                  />
                  {errors.cardHolderName && <p className="text-red-500 text-xs mt-1">{errors.cardHolderName}</p>}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <FaCreditCard className="mr-2" /> Card Number
                  </label>
                  <div className="relative flex items-center border rounded-full px-4 py-2 bg-transparent">
                    {renderCardIcon()}
                    <input
                        type="text"
                        name="cardNumber"
                        value={settings.cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="1234-5678-9012-3456"
                        className="w-full focus:outline-none bg-transparent"
                    />
                  </div>
                  {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center">
                      <FaLock className="mr-2" /> CVV
                      <span className="ml-2 text-gray-500 cursor-pointer group relative">
                      <FaInfoCircle />
                      <span className="hidden group-hover:block absolute left-0 top-6 bg-gray-800 text-white text-xs p-2 rounded z-10 w-48">
                        {cardType === "amex"
                            ? "4-digit code on the front of your card"
                            : "3-digit code on the back of your card"}
                      </span>
                    </span>
                    </label>
                    <input
                        type="password"
                        name="cvv"
                        value={settings.cvv}
                        onChange={handleCvvChange}
                        placeholder={cardType === "amex" ? "1234" : "123"}
                        className="w-full border rounded-full px-4 py-2 bg-transparent"
                    />
                    {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Expiration Date</label>
                    <input
                        type="text"
                        name="expiryDate"
                        value={settings.expiryDate}
                        onChange={handleExpiryDateChange}
                        placeholder="MM/YY"
                        className="w-full border rounded-full px-4 py-2 bg-transparent"
                    />
                    {errors.expiryDate && <p className="text-red-500 text-xs mt-1">{errors.expiryDate}</p>}
                  </div>
                </div>

                {/* Billing Information Section */}
                <div className="mt-4">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaHome className="mr-2 text-blue-600" /> Billing Information
                  </h2>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Billing Address</label>
                  <input
                      type="text"
                      name="billingAddress"
                      value={settings.billingAddress}
                      onChange={handleChange}
                      placeholder="123 Main St"
                      className="w-full border rounded-full px-4 py-2 bg-transparent"
                  />
                  {errors.billingAddress && <p className="text-red-500 text-xs mt-1">{errors.billingAddress}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                        type="text"
                        name="city"
                        value={settings.city}
                        onChange={handleChange}
                        placeholder="City"
                        className="w-full border rounded-full px-4 py-2 bg-transparent"
                    />
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">ZIP/Postal Code</label>
                    <input
                        type="text"
                        name="zipCode"
                        value={settings.zipCode}
                        onChange={handleChange}
                        placeholder="12345"
                        className="w-full border rounded-full px-4 py-2 bg-transparent"
                    />
                    {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <FaGlobe className="mr-2" /> Country
                  </label>
                  <select
                      name="country"
                      value={settings.country}
                      onChange={handleChange}
                      className="w-full border rounded-full px-4 py-2 bg-transparent"
                  >
                    {countries.map((country, index) => (
                        <option key={index} value={country.label}>
                          {country.label}
                        </option>
                    ))}
                  </select>
                </div>

                {/* Contact Information Section */}
                <div className="mt-4">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaEnvelope className="mr-2 text-blue-600" /> Contact Information
                  </h2>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <FaEnvelope className="mr-2" /> Email
                  </label>
                  <input
                      type="email"
                      name="email"
                      value={settings.email}
                      onChange={handleChange}
                      placeholder="john.doe@example.com"
                      className="w-full border rounded-full px-4 py-2 bg-transparent"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <FaPhone className="mr-2" /> Phone Number
                  </label>
                  <div className="flex flex-wrap">
                    <select
                        name="phoneCountryCode"
                        value={settings.phoneCountryCode}
                        onChange={handleChange}
                        className="border rounded-l-full px-3 py-2 bg-transparent w-24"
                    >
                      <option value="+94">+94</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+61">+61</option>
                      <option value="+91">+91</option>
                      <option value="+86">+86</option>
                    </select>
                    <input
                        type="text"
                        name="phoneNumber"
                        value={settings.phoneNumber}
                        onChange={handleChange}
                        placeholder="70124615"
                        className="flex-grow border rounded-r-full px-4 py-2 bg-transparent border-l-0"
                    />
                  </div>
                  {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                </div>

                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        name="saveCard"
                        checked={settings.saveCard}
                        onChange={handleChange}
                        className="form-checkbox h-5 w-5 text-blue-600 rounded"
                    />
                    <span className="text-sm">Save this card for future payments</span>
                  </label>
                </div>
              </div>

              <div className="mt-6 bg-yellow-100 border text-yellow-800 text-sm rounded-lg px-4 md:px-6 py-3">
                <div className="flex items-center">
                  <FaInfoCircle className="mr-2 flex-shrink-0" />
                  <span>
                  Billing will be available through one month. Your payment information is encrypted and secure.
                </span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-4 justify-center md:justify-start">
                <button
                    type="button"
                    className="bg-white border border-blue-500 text-blue-500 py-2 px-6 rounded-full hover:bg-blue-50 transition-colors w-full md:w-auto"
                    onClick={() =>
                        setSettings({
                          cardHolderName: "",
                          cardNumber: "",
                          cvv: "",
                          expiryDate: "",
                          billingAddress: "",
                          city: "",
                          zipCode: "",
                          email: "",
                          phoneCountryCode: "+94",
                          phoneNumber: "",
                          country: "Sri Lanka",
                          saveCard: false,
                        })
                    }
                >
                  Reset Form
                </button>
                <button
                    type="button"
                    className="bg-blue-500 text-white py-2 px-6 rounded-full hover:bg-blue-600 transition-colors w-full md:w-auto"
                    onClick={() => console.log("Payment method confirmed")}
                >
                  Verify Card
                </button>
                <button
                    type="submit"
                    className="bg-black text-white py-2 px-6 rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center w-full md:w-auto"
                    disabled={loading}
                >
                  {loading ? (
                      <>
                        <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                          <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                          ></circle>
                          <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                  ) : (
                      "Confirm Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};

export default SettingsPage;